import { IToken } from './token';
import { ActiveDirectoryType } from './enums';
import { IAzureMetadata, IOpenIdConnectMetadata, ISaveTenant, IValidationResult } from './models';

export abstract class MetadataClient {
	private static _storageId: string;
	static get storageId(): string {
		return MetadataClient._storageId;
	}

	private static _tenant: string;
	static get tenant(): string {
		return MetadataClient._tenant;
	}

	public static onRequestLogin: () => void;
	public static onAccessDenied: () => void;
	public static onBadRequest: () => void;
	public static onTokenRequest: () => void;
	public static onSetupTwoFactorRequest: (userId) => void;
	public static onTokenChanged: (token: IToken) => void;

	static get endpointsUrl(): string {
		return '../';
	}

	protected static refreshTokenPromise: Promise<IToken>;

	public static initialize(storageId: string, tenant: string) {
		MetadataClient._storageId = storageId;
		if (tenant)
			MetadataClient._tenant = tenant.toLowerCase();
	}

	protected static async handleError(response: Response): Promise<Response> {

		if (!response.ok) {
			if (response.status === 401)
				MetadataClient.reLogin();
			else if (response.status === 400) {
				const result = await response.json();
				if (result.error === 'token_required')
					MetadataClient.onTokenRequest();
				else if (result.error === 'setup_twofactor')
					MetadataClient.onSetupTwoFactorRequest(result.error_description);
				else
					MetadataClient.badRequest();
			}
			else
				throw new Error(`${response.status} - ${response.statusText}`);

			return;
		}

		return response;
	}

	public static async isValidActiveDirectory(saveTenant: ISaveTenant) {
		return MetadataClient.ajax<IValidationResult>(
			new Request(MetadataClient.endpointsUrl + `api/v1/activedirectory/isValid`, {
				headers: new Headers({ 'Content-Type': 'application/json; charset=utf-8' }),
				method: 'POST',
				body: JSON.stringify(saveTenant)
			})
		);
	}

	public static autoLoginActiveDirectory(code: string, tenant: string, clientId: string): Promise<any> {

		let headers = {
			Tenant: tenant,
			Code: code,
			RedirectUri: window.location.href
		}

		let request = new Request(`${MetadataClient.endpointsUrl}api/v1/oauth2/token`, {
			headers: new Headers(headers),
			body: new URLSearchParams({
				grant_type: 'password',
				username: "null",
				password: "null",
				client_id: clientId
			} as any),
			method: 'POST'
		});

		return fetch(request)
			.then(MetadataClient.handleError)
			.then(r => r.json())
			.then(MetadataClient.setToken);
	}

	public static login(userName: string, password: string, clientId: string, token?: string): Promise<any> {

		let headers = MetadataClient.tenant ? { Tenant: MetadataClient.tenant } : {};

		let body = new URLSearchParams({
			grant_type: 'password',
			username: userName,
			password: password,
			client_id: clientId
		} as any);

		if (token)
			body.append('token', token);

		let request = new Request(`${MetadataClient.endpointsUrl}api/v1/oauth2/token`, {
			headers: new Headers(headers),
			body: body,
			method: 'POST'
		});

		return fetch(request)
			.then(MetadataClient.handleError)
			.then(r => r.json())
			.then(MetadataClient.setToken);
	}

	public static logout() {
		var token = MetadataClient.getToken();
		if (token != null) {
			MetadataClient.revokeRefreshToken(token.refresh_token, token.access_token)
				.then(() => { }, () => { })
				.then(() => {
					window.localStorage.removeItem(MetadataClient.storageId);
				});
		}
	}

	public static getToken(): IToken {
		return JSON.parse(window.localStorage.getItem(MetadataClient.storageId));
	}

	public static hasToken(): boolean {
		const token = window.localStorage.getItem(MetadataClient.storageId);
		return !(!token || 0 === token.length);
	}

	protected static refreshToken(refreshToken: string, clientId: string): Promise<IToken> {

		let headers = MetadataClient.tenant ? { Tenant: MetadataClient.tenant } : {};

		let request = new Request(`${MetadataClient.endpointsUrl}api/v1/oauth2/token`, {
			headers: new Headers(headers),
			body: new URLSearchParams({
				grant_type: 'refresh_token',
				refresh_token: refreshToken,
				client_id: clientId
			} as any),
			method: 'POST'
		})

		return fetch(request)
			.then(MetadataClient.handleError)
			.then(r => (r.json() as Promise<IToken>));
	}

	protected static revokeRefreshToken(refreshToken: string, accessToken: string): Promise<IToken> {

		let request = new Request(`${MetadataClient.endpointsUrl}api/v1/oauth2/token/revoke`,
			{
				body: new URLSearchParams({
					grant_type: 'refresh_token',
					refresh_token: refreshToken
				} as any),
				method: 'POST'
			});

		return MetadataClient.ajaxWithAuthorization(request, accessToken);
	}

	public static ajax<T>(request: Request, anonymous: boolean = false, byPass: boolean = false): Promise<T> {

		if (anonymous)
			return MetadataClient.ajaxWithoutAuthorization(request);

		var promise: Promise<T>;
		var token = MetadataClient.getToken();

		if (!token || (token.initialRecharge === 'true' && !byPass)) {
			MetadataClient.requestLogin();
			// Can we do bettter than returning empty Promise?
			return new Promise((resolve, reject) => { });
		}

		if (Date.now() >= token.expiration_date) {
			if (!MetadataClient.refreshTokenPromise) {
				MetadataClient.refreshTokenPromise = MetadataClient.refreshToken(
					token.refresh_token,
					token.client_id
				);
			}

			return MetadataClient.refreshTokenPromise
				.then((newToken: IToken) => {
					MetadataClient.setToken(newToken);
					promise = MetadataClient.ajaxWithAuthorization<T>(request, newToken.access_token);
				})
				.catch(() => {
					MetadataClient.reLogin();
				})
				.then(() => {
					delete MetadataClient.refreshTokenPromise;
					return promise;
				});
		} else {
			return MetadataClient.ajaxWithAuthorization(request, token.access_token);
		}
	}

	public static isTokenValid(): Promise<boolean> {
		return new Promise((resolve, reject) => {
			var token = MetadataClient.getToken();
			if (!token || token.initialRecharge === 'true') {
				reject(false);
			}

			if (Date.now() >= token.expiration_date) {
				if (!MetadataClient.refreshTokenPromise) {
					MetadataClient.refreshTokenPromise = MetadataClient.refreshToken(
						token.refresh_token,
						token.client_id
					);
				}

				MetadataClient.refreshTokenPromise
					.then((newToken: IToken) => {
						MetadataClient.setToken(newToken);
						resolve(true);
					})
					.catch(() => {
						reject(false);
					})
					.then(() => {
						delete MetadataClient.refreshTokenPromise;
					});
			} else {
				resolve(true);
			}
		});
	}

	protected static ajaxWithAuthorization<T>(request: Request, accessToken: string): Promise<T> {

		request.headers.append('Authorization', 'Bearer '.concat(accessToken));
		return MetadataClient.ajaxWithoutAuthorization(request);
	}

	protected static ajaxWithoutAuthorization<T>(request: Request): Promise<T> {

		if (MetadataClient.tenant)
			request.headers.append('Tenant', MetadataClient.tenant);

		return fetch(request)
			.then(MetadataClient.handleError)
			.then(response => {

				let json = response.headers && (response.headers.get('Content-Type') || '').toLowerCase().startsWith('application/json');

				if (json)
					return response.json().catch(() => { });

				return response;
			});
	}

	protected static setToken(token: IToken) {
		token.expiration_date = Date.now() + token.expires_in * 1000;
		window.localStorage.setItem(MetadataClient.storageId, JSON.stringify(token));

		if (MetadataClient.onTokenChanged) {
			MetadataClient.onTokenChanged(token);
		}
	}

	protected static requestLogin() {
		if (MetadataClient.onRequestLogin) MetadataClient.onRequestLogin();
	}

	protected static accessDenied() {
		if (MetadataClient.onAccessDenied) {
			MetadataClient.onAccessDenied();
		}
	}

	protected static badRequest() {
		if (MetadataClient.onBadRequest) {
			MetadataClient.onBadRequest();
		}
	}

	protected static reLogin<T>(): Promise<T> {
		MetadataClient.logout();
		MetadataClient.requestLogin();
		return Promise.resolve(null);
	}
}
